/**
 * useHomeTour
 * Tour for the home page (first-time visitors)
 */

import { Tour } from '@shared/lib/tours/types'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useEffect } from 'react'
import { usePageReadyForTour } from './usePageReadyForTour'
import { markTourAsCompleted, useTourManager } from './useTourManager'

export function useHomeTour(autoStart: boolean = false): Tour {
  const { isTourCompleted, shouldShowTour, resetTour } = useTourManager()
  const isPageReady = usePageReadyForTour()
  const TOUR_NAME = 'home'

  const startTour = () => {
    const homeTour = driver({
      showProgress: true,
      nextBtnText: 'Siguiente',
      prevBtnText: 'Anterior',
      doneBtnText: '¡Listo!',
      progressText: '{{current}} de {{total}}',

      onDestroyed: () => {
        // Mark as completed when tour is destroyed
        // This ensures persistence even if user closes the tour early
        markTourAsCompleted(TOUR_NAME)
      },

      steps: [
        {
          popover: {
            title: '¡Bienvenido a ProFessionals!',
            description:
              'Te mostraremos cómo encontrar el profesional perfecto para tu proyecto. Este tour solo tomará un momento.',
          },
        },
        {
          element: '[data-tour="search-bar"]',
          popover: {
            title: 'Busca Rápidamente',
            description:
              'Usá la barra de búsqueda para encontrar profesiones específicas. Los resultados se actualizan en tiempo real.',
            side: 'bottom',
          },
        },
        {
          element: '[data-tour="professions-grid"]',
          popover: {
            title: 'Explora Profesiones',
            description:
              'Navegá por las categorías de profesionales disponibles. Hacé clic en cualquier profesión para ver los expertos.',
            side: 'bottom',
          },
        },
        {
          element: '[data-tour="navbar"]',
          popover: {
            title: 'Navegación',
            description:
              'Usá estos botones para navegar por la app.',
            side: 'top',
          },
        },
        {
          element: '[data-tour="navbar-profile"]',
          popover: {
            title: 'Tu Cuenta',
            description:
              'Desde acá podés acceder a tu perfil, gestionar tus datos y activar tu perfil profesional si ofrecés servicios.',
            side: 'top',
          },
        },
      ],
    })

    homeTour.drive()
  }

  // Auto-start tour on mount if conditions are met
  useEffect(() => {
    // Only start tour when page is ready (loaded and no PWA prompt)
    if (!isPageReady) return

    if (autoStart && shouldShowTour(TOUR_NAME)) {
      // Small delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        startTour()
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [autoStart, isPageReady])  

  const reset = () => {
    resetTour(TOUR_NAME)
  }

  return {
    startTour,
    isCompleted: isTourCompleted(TOUR_NAME),
    reset,
  }
}
